import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { login, verifyMfa } from '@/api/auth/auth.api'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { isMfaChallengeResponse, LoginSuccessResponse } from '@/api/auth/types/LoginResponse'
import { EMPTY_MFA_DIGITS, focusMfaCodeInput, MfaCodeInput } from '@/components/mfa/MfaCodeInput'
import { PasswordInput } from '@/components/PasswordInput'
import { useAuth } from '@/context/AuthContext'
import { openExternalUrl, SIGNUP_URL } from '@/lib/native'
import { setAuthTokensSync } from '@/lib/native/authTokens'
import { QUERY_KEYS } from '@/lib/queryKeys'

import packageJson from '../../../../package.json'
import { TextField } from '../../../components/TextField'
import { Button } from '../../../components/ui/button'
import { OfficeRoutePath, RoutePath } from '../../../RoutePath'
import LoginIcon from './login-icon.svg?react'

const initialFormState = {
  username: '',
  password: '',
}

type LoginStep = 'credentials' | 'mfaChallenge'

const getPostLoginRoute = (permissions?: Record<string, boolean>) => {
  if (permissions?.AffiliateManager) return OfficeRoutePath.AffiliateManager
  if (permissions?.PickupOrderDriver) return OfficeRoutePath.ScanPickupOrders
  return RoutePath.ClientAddresses
}

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setIsAuthenticated } = useAuth()
  const location = useLocation()
  const [formState, setFormState] = useState(initialFormState)
  const [isLoadingSignIn, setIsLoadingSignIn] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [loginStep, setLoginStep] = useState<LoginStep>('credentials')
  const [mfaSession, setMfaSession] = useState('')
  const [mfaUsername, setMfaUsername] = useState('')
  const [mfaCodeDigits, setMfaCodeDigits] = useState<string[]>([...EMPTY_MFA_DIGITS])
  const isSubmittingMfa = useRef(false)
  const mfaInputIdPrefix = 'mfa-login-digit'
  const { data: loggedUser } = useCurrentUser()
  const queryClient = useQueryClient()

  useEffect(() => {
    const logoutReason = localStorage.getItem('logout-reason')
    if (logoutReason === 'inactivity') {
      localStorage.removeItem('logout-reason')
    }
  }, [])

  useEffect(() => {
    if (loggedUser && loginStep === 'credentials') {
      navigate(getPostLoginRoute(loggedUser.permissions))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist()
    const { name, value } = e.target
    setFormState(prev => ({ ...prev, [name]: value }))
    setErrorMessage('')
  }

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const completeLogin = (userData: LoginSuccessResponse['data'], token: LoginSuccessResponse['token']) => {
    queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], userData)
    setAuthTokensSync(token)
    setIsAuthenticated(true)

    if (location.pathname === RoutePath.Login || location.pathname === RoutePath.Welcome || location.pathname === '/') {
      navigate(getPostLoginRoute(userData?.permissions))
    }
  }

  const signIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      if (!formState.username) {
        setEmailError(t('login.emailRequired'))
        return
      }
      if (!isValidEmail(formState.username)) {
        setEmailError(t('login.emailInvalid'))
        return
      }
      if (!formState.password) {
        setPasswordError(t('login.passwordRequired'))
        return
      }
      setIsLoadingSignIn(true)
      setErrorMessage('')
      const { username, password } = formState

      const result = await login(username, password, packageJson.version)

      if (isMfaChallengeResponse(result)) {
        setMfaSession(result.session)
        setMfaUsername(result.username)
        setMfaCodeDigits([...EMPTY_MFA_DIGITS])
        isSubmittingMfa.current = false
        setLoginStep('mfaChallenge')
        setIsLoadingSignIn(false)
        return
      }

      completeLogin(result.data, result.token)
      setIsLoadingSignIn(false)
    } catch (error) {
      setIsLoadingSignIn(false)
      const message = (error as { msg: string }).msg
      if (message) {
        setErrorMessage(message)
      } else {
        setErrorMessage(t('login.networkError'))
      }
    }
  }

  const submitMfaCode = async (code: string) => {
    if (code.length !== 6 || isSubmittingMfa.current) return
    isSubmittingMfa.current = true
    try {
      setIsLoadingSignIn(true)
      setErrorMessage('')
      const result = await verifyMfa(mfaUsername, mfaSession, code)
      completeLogin(result.data, result.token)
      setIsLoadingSignIn(false)
    } catch (error) {
      isSubmittingMfa.current = false
      setIsLoadingSignIn(false)
      const message = (error as { msg: string }).msg
      setErrorMessage(message || t('mfa.invalidCode'))
      setMfaCodeDigits([...EMPTY_MFA_DIGITS])
      focusMfaCodeInput(mfaInputIdPrefix)
    }
  }

  const onMfaDigitsChange = (digits: string[]) => {
    setMfaCodeDigits(digits)
    setErrorMessage('')
  }

  const submitMfaChallenge = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = mfaCodeDigits.join('')
    if (code.length !== 6) {
      setErrorMessage(t('mfa.codeRequired'))
      return
    }
    await submitMfaCode(code)
  }

  return (
    <div className='flex items-center justify-center p-6'>
      <div className='flex w-full max-w-[480px] flex-col justify-center rounded-2xl bg-white p-6 shadow-sm'>
        <div className='mb-6 text-center'>
          <div className='mx-auto mb-8 flex h-[140px] w-[140px] items-center justify-center'>
            <LoginIcon />
          </div>
          <h1 className='text-lg font-medium text-gray-900'>
            {loginStep === 'mfaChallenge' ? t('mfa.challengeTitle') : t('login.welcomeBack')}
          </h1>
          {loginStep === 'mfaChallenge' && <p className='mt-2 text-sm text-gray-600'>{t('mfa.challengeDescription')}</p>}
        </div>

        {errorMessage && (
          <div className='mb-6 rounded-lg border border-red-200 bg-red-50 p-4'>
            <p className='text-center text-sm text-red-600'>{errorMessage}</p>
          </div>
        )}

        {loginStep === 'credentials' && (
          <form onSubmit={signIn}>
            <div className='mb-2 flex flex-col gap-4'>
              <TextField
                label={t('login.email')}
                type='email'
                value={formState.username}
                onChange={onChange}
                name='username'
                error={!!emailError}
                supportingText={emailError}
              />

              <PasswordInput
                value={formState.password}
                onChange={onChange}
                label={t('login.password')}
                name='password'
                error={!!passwordError}
                supportingText={passwordError}
              />
            </div>

            <Link to={RoutePath.ForgotPassword}>
              <Button type='button' variant='link' className='mb-6 text-xs'>
                {t('login.forgotPassword')}
              </Button>
            </Link>

            <Button type='submit' loading={isLoadingSignIn} disabled={isLoadingSignIn} size='lg' className='w-full'>
              {t('login.signIn')}
            </Button>

            <div className='mt-4 space-y-4'>
              <p className='text-center text-xs text-gray-600'>
                {t('login.newUser')}{' '}
                <Button type='button' variant='link' className='text-xs' onClick={() => void openExternalUrl(SIGNUP_URL)}>
                  {t('login.registerHere')}
                </Button>
              </p>
            </div>
          </form>
        )}

        {loginStep === 'mfaChallenge' && (
          <form onSubmit={submitMfaChallenge} className='space-y-6'>
            <div className='flex justify-center'>
              <MfaCodeInput
                idPrefix={mfaInputIdPrefix}
                value={mfaCodeDigits}
                onChange={onMfaDigitsChange}
                onComplete={code => void submitMfaCode(code)}
                disabled={isLoadingSignIn}
              />
            </div>

            <Button type='submit' loading={isLoadingSignIn} disabled={isLoadingSignIn} size='lg' className='w-full'>
              {t('mfa.verify')}
            </Button>

            <Button
              type='button'
              variant='link'
              className='w-full'
              onClick={() => {
                setLoginStep('credentials')
                setMfaSession('')
                setMfaCodeDigits([...EMPTY_MFA_DIGITS])
                isSubmittingMfa.current = false
                setErrorMessage('')
              }}
            >
              {t('mfa.backToSignIn')}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
